<template>
  <v-dialog 
    :value="dialog" 
    persistent 
    max-width="500px">
    <v-card v-if="item && item.type == 'dateblock'">
      <v-toolbar 
        color="primary" 
        dark>
        <v-toolbar-title>
          <span class="headline">Add date picker</span>            
        </v-toolbar-title>
      </v-toolbar>        
      <v-card 
        color="secondary lighten-5">
        <v-layout
          row 
          wrap>
          <v-flex
            sm12
            md1
            class="secondary"
          >    
            <v-layout 
              align-center 
              justify-center 
              fill-height 
              row>       
              <v-tooltip bottom>
                <v-icon slot="activator">visibility</v-icon>
                <span>Preview</span>
              </v-tooltip>
            </v-layout>
          </v-flex>
          <v-flex 
            sm12 
            md11>
            <v-card-text> 
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                max-width="290"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value=" date
                    ? $moment(date).format('dddd, MMMM Do YYYY') : ''"
                    clearable
                    :label="item.label"
                    readonly
                    :rules="[
                    v => !item.required || !!v || 'Required' ]"
                    v-on="on"
                    :hint="item.hint"
                    :persistent-hint="item.persistantHint"
                    :required="item.required" 
                    prepend-icon="event"
                  />
                </template>
                <v-date-picker
                  v-model="date"
                  @change="dateMenu = false"
                />
              </v-menu>
            </v-card-text>
          </v-flex>
        </v-layout>  
      </v-card> 
      <v-form 
        ref="form"
        v-model="valid"              
        lazy-validation>         
        <v-card-text>                
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-text-field
                  v-model="item.label"
                  :rules="labelRules"
                  label="Label"                                           
                  required
                  hint="Label for text field"  
                />
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="item.hint"
                  :rules="[
                    v => !v || v.length < 100 || 'Text must be less than 100 characters'
                  ]"
                  label="Hint"                                           
                  hint="Hint appears under text field when selected or always if persistant"  
                />
              </v-flex>
              <v-flex xs12>
                <v-checkbox 
                  v-model="item.persistantHint" 
                  label="Persistant hint (always show hint)"/>
              </v-flex>
              <v-flex xs12>
                <v-radio-group 
                  v-model="item.required"
                  persistent-hint
                  hint="Is the text field mandatory"
                  row 
                >
                  <v-radio
                    :value="true"
                    label="Required"                      
                    color="primary" 
                  />
                  <v-radio
                    :value="false"
                    label="Optional"                      
                    color="primary" 
                  />
                </v-radio-group>                
              </v-flex>
              <v-flex xs12>
                <v-radio-group 
                  v-model="item.show"
                  persistent-hint
                  hint="Should the element be displayed when editing a form or always"
                  row 
                >
                  <v-radio
                    :value="true"
                    label="Show always"                      
                    color="primary" 
                  />
                  <v-radio
                    :value="false"
                    label="Show in form"                      
                    color="primary" 
                  />
                </v-radio-group>                
              </v-flex>  
              <v-flex xs12>
                <v-select
                  v-model="item.hide"
                  :items="['Student', 'Organisation','Staff','Academic']"
                  attach
                  chips
                  label="Hide from"
                  multiple
                  hint="Users from the selected groups are unable to see this field"
                />
              </v-flex>
              <v-flex xs12>
                <v-checkbox 
                  v-model="item.csv" 
                  label="Include element in CSV"/>
              </v-flex>
              <v-flex 
                xs12 
                v-if="item.csv"> 
                <v-btn 
                  v-if="suggestedKey && suggestedKey != item.csvKey" 
                  block
                  @click="item.csvKey = suggestedKey"
                  :value="suggestedKey">Use key: "{{ suggestedKey }}"</v-btn>              
                <v-text-field
                  :rules="csvRules"
                  v-model="item.csvKey"                  
                  label="CSV key"  
                  persistent-hint
                  hint="CSV headers are generated from these Keys and must be unique"
                />                
              </v-flex>           
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn 
            color="primary" 
            flat 
            @click.native="close">Close</v-btn>
          <v-btn 
            v-if="dateitem.label" 
            color="red darken-1" 
            flat 
            @click.native="remove()">Delete</v-btn>         
          <v-btn 
            color="primary" 
            flat 
            @click.native="save(item)">{{ (dateitem.label ? 'Update' : 'Save') }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "Dateblock",
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    dateitem: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      valid: true,
      dateMenu: false,
      date: null,
      checkbox: null,
      labelRules: [
        v => !!v || "Required",
        v => (!!v && v.length < 100) || "Text must be less than 100 characters"
      ],
      maxRules: [
        v =>
          /^([0-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-5][0-9][0-9][0-9])$/.test(v) ||
          "Must be less than 6000 characters"
      ],
      minRules: [
        v =>
          /^([0-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-2][0-9][0-9][0-9])$/.test(v) ||
          "Must be less than 3000 characters"
      ],
      csvRules: [
        v => !!v || "Required",
        v => /^[a-zA-Z0-9_]+$/.test(v) || "Alphanumeric and underscore"
      ],
      item: null
    };
  },
  watch: {
    dateitem: function(val) {
      this.item = JSON.parse(JSON.stringify(val));
    }
  },
  computed: {
    suggestedKey: function() {
      if (this.item && this.item.label) {
        return this.item.label
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_]/g, "")
          .toUpperCase();
      }
      return null;
    }
  },
  methods: {
    save(item) {
      if (this.$refs.form.validate()) {
        this.$emit("statusDialog", false);
        this.$emit("saveItem", item);
        this.$store.dispatch("setLoading", true);
      }
    },
    remove() {
      this.$emit("statusDialog", false);
      this.$emit("saveItem", null);
      this.$store.dispatch("setLoading", true);
    },
    close() {
      this.$emit("statusDialog", false);
      this.$store.dispatch("sendNotification", {
        snackbar: true,
        color: "warning",
        text: "Date picker not saved"
      });
    }
  }
};
</script>
